import { useState } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';

import { RouterLink } from 'src/routes/components';

import { fUntilNow } from 'src/utils/format-time';

import { CONFIG } from 'src/global-config';
import { useTranslate } from 'src/locales';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { FileThumbnail } from 'src/components/file-thumbnail';

// ----------------------------------------------------------------------

export function NotificationItem({ notification, collection, handleMarkAsRead }) {
  const { t } = useTranslate();

  const [isUnRead, setIsUnRead] = useState(notification.isUnRead);

  if (!((collection === 'unread' && isUnRead) || collection === 'all')) return null;

  const onHandleMarkAsRead = () => {
    setIsUnRead(false);
    handleMarkAsRead?.(notification.id);
  };

  const renderAvatar = () => (
    <ListItemAvatar>
      {notification.avatarUrl === null ? (
        <Box
          sx={{
            width: 42,
            height: 42,
            display: 'flex',
            borderRadius: '50%',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'background.neutral',
          }}
        >
          <Box
            component="img"
            src={`${CONFIG.assetsDir}/assets/icons/notification/ic-${notification.type}.svg`}
            sx={{ width: 25, height: 25 }}
          />
        </Box>
      ) : (
        <Avatar src={notification.avatarUrl} />
      )}
    </ListItemAvatar>
  );

  const renderText = () => (
    <ListItemText
      disableTypography
      primary={reader(notification.title, { maxWidth: { xs: 0.88, md: 0.8 } })}
      secondary={
        <Stack
          sx={{
            alignItems: 'center',
            flexDirection: 'row',
            typography: 'caption',
            color: 'text.disabled',
          }}
        >
          {fUntilNow(notification.createdAt.toDate())}
        </Stack>
      }
    />
  );

  const renderUnReadBadge = () => (
    <Box sx={{ top: 16, right: 16, position: 'absolute' }}>
      <Tooltip title={t(isUnRead ? 'Mark as read' : 'Readed')}>
        <IconButton
          color={isUnRead ? 'inherit' : 'info'}
          onClick={() => isUnRead && onHandleMarkAsRead}
          sx={{ color: isUnRead ? 'text.disabled' : 'info.main' }}
        >
          <Iconify icon="eva:done-all-fill" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const renderAction = (title = 'View', variant = 'contained') => (
    <Stack spacing={1} direction="row" sx={{ mt: 1.5 }}>
      <Button size="small" variant={variant} component={RouterLink} href={notification.action}>
        {t(title)}
      </Button>
    </Stack>
  );

  const renderFriendAction = () => (
    <Box sx={{ gap: 1, mt: 1.5, display: 'flex' }}>
      <Button size="small" variant="contained">
        Accept
      </Button>
      <Button size="small" variant="outlined">
        Decline
      </Button>
    </Box>
  );

  const renderProjectAction = () => (
    <>
      <Box
        sx={{
          p: 1.5,
          my: 1.5,
          borderRadius: 1.5,
          color: 'text.secondary',
          backgroundColor: 'background.neutral',
        }}
      >
        {reader(
          `<p><strong>@Jaydon Frankie</strong> feedback by asking questions or just leave a note of appreciation.</p>`
        )}
      </Box>

      <Button size="small" variant="contained" sx={{ alignSelf: 'flex-start' }}>
        Reply
      </Button>
    </>
  );

  const renderFileAction = () => (
    <Box
      sx={{
        pl: 1,
        gap: 1,
        p: 1.5,
        mt: 1.5,
        display: 'flex',
        borderRadius: 1.5,
        backgroundColor: 'background.neutral',
      }}
    >
      <FileThumbnail file="http://localhost:8080/httpsdesign-suriname-2015.mp3" />

      <Box
        sx={{
          gap: 1,
          flexGrow: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <ListItemText
          disableTypography
          primary={
            <Typography variant="subtitle2" component="div" sx={{ color: 'text.secondary' }} noWrap>
              design-suriname-2015.mp3
            </Typography>
          }
          secondary={
            <Stack
              divider={
                <Box
                  sx={{
                    mx: 0.5,
                    width: 2,
                    height: 2,
                    borderRadius: '50%',
                    backgroundColor: 'currentColor',
                  }}
                />
              }
              sx={{
                alignItems: 'center',
                typography: 'caption',
                color: 'text.disabled',
                flexDirection: 'row',
              }}
            >
              <span>2.3 GB</span>
              <span>30 min ago</span>
            </Stack>
          }
        />

        <Button size="small" variant="outlined">
          Download
        </Button>
      </Box>
    </Box>
  );

  const renderValueAction = () => (
    <Box
      sx={{
        mt: 1.5,
        gap: 0.75,
        display: 'flex',
        flexWrap: 'wrap',
      }}
    >
      <Label variant="outlined" color="info">
        Design
      </Label>
      <Label variant="outlined" color="warning">
        Dashboard
      </Label>
      <Label variant="outlined">Design system</Label>
    </Box>
  );

  return (
    <ListItemButton
      disableRipple
      sx={[
        (theme) => ({
          p: 2.5,
          alignItems: 'flex-start',
          borderBottom: `dashed 1px ${theme.vars.palette.divider}`,
        }),
      ]}
    >
      {renderUnReadBadge()}

      {renderAvatar()}

      <Stack sx={{ flexGrow: 1 }}>
        {renderText()}
        {notification.type === 'order' && renderAction('View order')}
        {notification.type === 'payment' && renderAction('See transfer')}
        {notification.type === 'social' && renderAction('View profile', 'outlined')}
        {notification.type === 'referred' && renderAction('View profile', 'outlined')}
        {notification.type === 'friend' && renderFriendAction()}
        {notification.type === 'project' && renderProjectAction()}
        {notification.type === 'file' && renderFileAction()}
        {notification.type === 'tags' && renderValueAction()}
      </Stack>
    </ListItemButton>
  );
}

function reader(data, sx) {
  return (
    <Box
      dangerouslySetInnerHTML={{ __html: data }}
      sx={[
        {
          mb: 0.5,
          '& p': { typography: 'body2', m: 0 },
          '& a': { color: 'inherit', textDecoration: 'none' },
          '& strong': { typography: 'subtitle2' },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );
}
